import { useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";

import {
  ApiReportSectionFieldsFieldTypeChoices,
  ReportSectionFieldsType,
  ReportSectionsType,
  useUpdateReportSectionFieldMutation,
} from "@api/graphql/types-and-hooks";
import { UpdateReportSectionFieldMutateOptions } from "@components/ReportField/types";
import { useUnsavedMarker } from "@components/ReportField/useUnsavedMarker";
import { notifyError } from "@components/notifications/notifications";
import { useReport } from "@providers/ReportProvider/useReport";

const DEBOUNCED_FIELD_TYPES: ReadonlySet<string> = new Set([
  ApiReportSectionFieldsFieldTypeChoices.SimpleText,
  ApiReportSectionFieldsFieldTypeChoices.Wysiwyg,
  ApiReportSectionFieldsFieldTypeChoices.TypedSignature,
  ApiReportSectionFieldsFieldTypeChoices.TreatmentOptionTable,
  ApiReportSectionFieldsFieldTypeChoices.AssessmentAppointments,
]);

const DEBOUDCED_TIMEOUT = 500;

export const useReportFieldAutosave = (section: ReportSectionsType, field: ReportSectionFieldsType) => {
  const { reportQuery } = useReport();
  const form = useFormContext<Record<string, string>>();
  const { mark: markUnsaved, clear: clearUnsaved } = useUnsavedMarker(`${field.id}:saving`);
  const savesInFlightRef = useRef(0);
  const updateField = useUpdateReportSectionFieldMutation({
    onSuccess: async (data, { content }) => {
      if (data.updateReportSectionField?.status !== false && form?.getValues(field.id) === content) {
        form.resetField(field.id, { defaultValue: content, keepTouched: true, keepError: true });
      }
      await reportQuery.refetch();
    },
    onError: (error) => {
      console.error("Error while updating field content:", error);
      notifyError("Something went wrong saving your changes. Please try again.");
    },
    onSettled: () => {
      savesInFlightRef.current -= 1;
      if (savesInFlightRef.current === 0 && !saveDebounced.isPending()) clearUnsaved();
    },
  });

  const saveNow = (content: string, mutateOptions?: UpdateReportSectionFieldMutateOptions) => {
    savesInFlightRef.current += 1;
    updateField.mutate({ reportSectionId: section.id, reportSectionFieldId: field.id, content }, mutateOptions);
  };
  const saveDebounced = useDebouncedCallback(saveNow, DEBOUDCED_TIMEOUT, { flushOnExit: true });

  const save = (content: string, mutateOptions?: UpdateReportSectionFieldMutateOptions) => {
    markUnsaved();
    if (DEBOUNCED_FIELD_TYPES.has(field.fieldType)) saveDebounced(content, mutateOptions);
    else saveNow(content, mutateOptions);
  };

  return { save, isPending: updateField.isPending };
};
